import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Image as ImageIcon, Video, Music, File, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ingestionService from '../../services/ingestionService';
import { supabase } from '../../lib/supabase';

export default function DragDropOverlay() {
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [dragCounter, setDragCounter] = useState(0);

  useEffect(() => {
    // Prevent default drag behavior on entire document
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setDragCounter(prev => prev + 1);
      
      if (e.dataTransfer.types.includes('Files')) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setDragCounter(prev => {
        const newCounter = prev - 1;
        if (newCounter === 0) {
          setIsDragging(false);
        }
        return newCounter;
      });
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      console.log('🎯 [DragDrop] Drop event detected!');
      
      setIsDragging(false);
      setDragCounter(0);

      if (!user?.id) {
        console.error('❌ [DragDrop] User not authenticated', { user });
        return;
      }

      console.log('✅ [DragDrop] User authenticated:', user.id);

      const files = Array.from(e.dataTransfer.files);
      console.log('📁 [DragDrop] Files dropped:', files.length, files.map(f => f.name));
      
      // Process each file
      for (const file of files) {
        const uploadId = `${Date.now()}-${Math.random()}`;
        
        // Add to upload queue
        setUploads(prev => [...prev, {
          id: uploadId,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'uploading',
          progress: 0
        }]);

        try {
          console.log(`🚀 [DragDrop] Starting upload for: ${file.name}`);
          
          // Check if file is an image
          const isImage = file.type.startsWith('image/');
          
          if (isImage) {
            console.log('🖼️ [DragDrop] Image detected - using deepsearch ingestion (same as Swirl It)');
            
            // For images: Upload to "items" bucket using the correct path format
            // CRITICAL: Path MUST include userId as first folder for RLS policies!
            const itemId = crypto.randomUUID();
            const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'png';
            const sanitizedExtension = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension) ? fileExtension : 'png';
            const storagePath = `${user.id}/${itemId}.${sanitizedExtension}`;
            
            console.log(`📤 [DragDrop] Uploading to "items" bucket at path: ${storagePath}`);
            
            // Upload file to "items" bucket
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('items')
              .upload(storagePath, file, {
                cacheControl: '3600',
                upsert: false
              });
            
            if (uploadError) {
              console.error('❌ [DragDrop] Storage upload error:', uploadError);
              throw new Error(`Failed to upload file: ${uploadError.message}`);
            }
            
            // Construct public URL
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const publicUrl = `${supabaseUrl}/storage/v1/object/public/items/${storagePath}`;
            console.log('✅ [DragDrop] File uploaded, public URL:', publicUrl);
            
            // Create item in deepsearch.items
            const { data: item, error: itemError } = await supabase
              .schema('deepsearch')
              .from('items')
              .insert({
                id: itemId,
                user_id: user.id,
                source_type: 'image',
                url: publicUrl,
                image_url: publicUrl,
                title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
                description: `Image file (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
                status: 'pending',
                meta: {
                  filename: file.name,
                  filesize: file.size,
                  mimetype: file.type,
                  source: 'drag_drop'
                }
              })
              .select()
              .single();
            
            if (itemError) {
              console.error('❌ [DragDrop] Item creation error:', itemError);
              throw new Error(`Failed to create item: ${itemError.message}`);
            }
            
            console.log('✅ [DragDrop] Item created:', itemId);
            
            // Queue job in deepsearch.ingest_queue for the worker to process
            // Provider 'image' tells the worker to run OCR + AI vision analysis
            const { data: job, error: jobError } = await supabase
              .schema('deepsearch')
              .from('ingest_queue')
              .insert({
                user_id: user.id,
                item_id: itemId,
                status: 'queued',
                provider: 'image'
              })
              .select()
              .single();
            
            if (jobError) {
              console.error('❌ [DragDrop] Job queue error:', jobError);
              // Don't throw - item is created, worker will pick it up eventually
            } else {
              console.log('✅ [DragDrop] Job queued:', job.id);
            }
            
            // Dispatch contentAdded event for real-time UI update
            window.dispatchEvent(new CustomEvent('contentAdded', {
              detail: {
                source: 'drag_drop',
                type: 'image',
                filename: file.name,
                item: item
              }
            }));
            
            console.log(`✅ [DragDrop] Image processing queued for: ${file.name}`);
          } else {
            // For non-images: Use existing upload flow
            console.log('📄 [DragDrop] Non-image file - using standard upload');
            
            await ingestionService.ingestUpload(user.id, file, (progress) => {
              console.log(`📊 [DragDrop] Progress for ${file.name}: ${Math.round(progress)}%`);
              setUploads(prev => prev.map(u => 
                u.id === uploadId 
                  ? { ...u, progress: Math.round(progress) }
                  : u
              ));
            });
          }
          
          console.log(`✅ [DragDrop] Upload complete for: ${file.name}`);

          // Mark as completed
          setUploads(prev => prev.map(u => 
            u.id === uploadId 
              ? { ...u, status: 'completed', progress: 100 }
              : u
          ));

          // Remove after 3 seconds
          setTimeout(() => {
            setUploads(prev => prev.filter(u => u.id !== uploadId));
          }, 3000);
        } catch (error) {
          console.error('❌ [DragDrop] Upload failed:', error);
          console.error('❌ [DragDrop] Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
          setUploads(prev => prev.map(u => 
            u.id === uploadId 
              ? { ...u, status: 'failed', error: error.message }
              : u
          ));

          // Remove after 5 seconds
          setTimeout(() => {
            setUploads(prev => prev.filter(u => u.id !== uploadId));
          }, 5000);
        }
      }
    };

    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [user]);

  // Get icon for file type
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return ImageIcon;
    if (type.startsWith('video/')) return Video;
    if (type.startsWith('audio/')) return Music;
    if (type === 'application/pdf') return FileText;
    return File;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      {/* Drag Overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] pointer-events-none"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-cyan-500/20 backdrop-blur-xl" />
            
            {/* Animated border */}
            <div className="absolute inset-4 border-4 border-dashed border-purple-500 rounded-3xl" />
            
            {/* Center content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.8, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                className="relative"
              >
                {/* Animated rings */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0.2, 0.5]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 blur-3xl"
                  style={{ width: '300px', height: '300px', left: '-75px', top: '-75px' }}
                />
                
                {/* Upload icon */}
                <div className="relative bg-white rounded-3xl shadow-2xl p-12 border-4 border-purple-200">
                  <motion.div
                    animate={{ 
                      y: [0, -10, 0]
                    }}
                    transition={{ 
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  >
                    <Upload size={80} className="text-purple-600" strokeWidth={1.5} />
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mt-6 text-center"
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Drop to Save to Swirl
                    </h2>
                    <p className="text-gray-600">
                      Images, videos, PDFs, and more
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Progress Notifications */}
      <AnimatePresence>
        {uploads.length > 0 && (
          <div className="fixed bottom-6 right-6 z-[10000] space-y-3 max-w-sm">
            {uploads.map((upload) => {
              const Icon = getFileIcon(upload.type);
              
              return (
                <motion.div
                  key={upload.id}
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 50, scale: 0.95 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 min-w-[320px]"
                >
                  <div className="flex items-start gap-3">
                    {/* File icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      upload.status === 'completed' 
                        ? 'bg-green-100' 
                        : upload.status === 'failed'
                        ? 'bg-red-100'
                        : 'bg-purple-100'
                    }`}>
                      {upload.status === 'completed' ? (
                        <CheckCircle size={20} className="text-green-600" />
                      ) : upload.status === 'failed' ? (
                        <AlertCircle size={20} className="text-red-600" />
                      ) : (
                        <Icon size={20} className="text-purple-600" />
                      )}
                    </div>
                    
                    {/* File details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {upload.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatFileSize(upload.size)}
                      </p>
                      
                      {/* Progress bar */}
                      {upload.status === 'uploading' && (
                        <div className="mt-2">
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${upload.progress}%` }}
                              transition={{ duration: 0.3 }}
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {upload.progress}%
                          </p>
                        </div>
                      )}
                      
                      {/* Status message */}
                      {upload.status === 'completed' && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          ✓ Saved to Swirl
                        </p>
                      )}
                      {upload.status === 'failed' && (
                        <p className="text-xs text-red-600 mt-1">
                          Upload failed
                        </p>
                      )}
                    </div>
                    
                    {/* Loading spinner */}
                    {upload.status === 'uploading' && (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <Loader size={16} className="text-purple-500" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </AnimatePresence>
    </>
  );
}



